import type { LucideIcon } from "lucide-react"

import { cn } from "~/lib/utils"

// Shared building blocks for the Figma "Landing Page — Internal Assets"
// section pattern (Insights 8:501, Console 8:492): a centred eyebrow /
// heading / sub-head header, a full-bleed product screenshot that fades into
// the section background, and a row of icon-chip cards beneath it.

// Forest DS "Caption Data" eyebrow — Roboto Flex, 14px, semibold, uppercase.
export function Eyebrow({
  children,
  tone = "dark",
  className,
}: {
  children: React.ReactNode
  tone?: "dark" | "light"
  className?: string
}) {
  return (
    <p
      className={cn(
        "font-flex text-[14px] font-semibold uppercase tracking-[0.08em]",
        tone === "dark" ? "text-cream" : "text-mute",
        className,
      )}
    >
      {children}
    </p>
  )
}

// The pine icon-chip (Forest DS "Icon Big" — bg #0D3C00). Renders the given
// Lucide icon in lime, or falls back to the DS location glyph.
export function IconChip({
  icon: Icon,
  className,
}: {
  icon?: LucideIcon
  className?: string
}) {
  return (
    <span
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] bg-pine",
        className,
      )}
    >
      {Icon ? (
        <Icon className="h-4 w-4 text-lime" strokeWidth={1.75} />
      ) : (
        <img
          src="/forest/icon-big-default.svg"
          alt=""
          loading="lazy"
          className="h-4 w-4"
        />
      )}
    </span>
  )
}

// Centred header used by both forest and cream sections.
export function SectionHeader({
  eyebrow,
  title,
  subtitle,
  tone = "dark",
  children,
}: {
  /** Optional: the alternative page sets none. */
  eyebrow?: string
  title: React.ReactNode
  subtitle: string
  tone?: "dark" | "light"
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      {eyebrow ? <Eyebrow tone={tone}>{eyebrow}</Eyebrow> : null}
      <h2
        className={cn(
          "max-w-3xl font-sans text-3xl font-medium leading-none tracking-tight sm:text-[40px]",
          tone === "dark" ? "text-hay" : "text-ink",
        )}
      >
        {title}
      </h2>
      <p
        className={cn(
          "max-w-xl text-[20px] font-normal leading-tight tracking-tight",
          // mute (#6C6C6C) on forest is 3.0:1; paper/70 is 8.3:1 (WCAG AA).
          tone === "dark" ? "text-paper/70" : "text-mute",
        )}
      >
        {subtitle}
      </p>
      {children ? <div className="pt-2">{children}</div> : null}
    </div>
  )
}

// Product screenshot for a section. `frame` object-covers the shot into a
// box for that section (the shot carries its own device frame + a transparent
// margin, so no background or rounding of our own):
//   insights → "Insights Platform" 1269 × 560 (8:506)
//   console  → a contained ~1000px element (Figma 8:492 has it at ~77% of the
//              frame), showing ~90% of the UI with the last sliver fading out
// `fade` (optional) draws the bottom gradient into the section colour.
const FRAMES = {
  insights: "max-w-[1269px] [&_img]:aspect-[1269/560]",
  // Same footprint as the Insights shot so the two product sections line up.
  console: "max-w-[1269px] [&_img]:aspect-[1269/560]",
} as const

export function BleedShot({
  src,
  srcSet,
  sizes,
  width,
  height,
  alt,
  frame,
  fade,
}: {
  src: string
  srcSet?: string
  sizes?: string
  width?: number
  height?: number
  alt: string
  frame?: keyof typeof FRAMES
  fade?: "forest" | "cream"
}) {
  const to = fade === "forest" ? "to-forest" : "to-cream"
  return (
    <div
      className={cn(
        "relative mx-auto w-full overflow-hidden",
        frame ? FRAMES[frame] : "max-w-6xl",
      )}
    >
      <img
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        width={width}
        height={height}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={cn(
          "block w-full object-cover object-top",
          !frame && "rounded-[10px]",
        )}
      />
      {fade ? (
        <div
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-b from-transparent",
            frame === "console" ? "h-1/6" : "h-1/4",
            to,
          )}
          aria-hidden
        />
      ) : null}
    </div>
  )
}

// Icon-chip card (Figma "Card" / purpose cards): chip, 20px title, 14px body.
export function ChipCard({
  title,
  body,
  icon,
  surface = "forest",
}: {
  title: React.ReactNode
  body: string
  icon?: LucideIcon
  surface?: "forest" | "white"
}) {
  return (
    <div
      className={cn(
        "flex flex-1 flex-col justify-center gap-2.5 rounded-[2px] px-6 py-5",
        surface === "forest" ? "bg-forest" : "bg-white",
      )}
    >
      <IconChip icon={icon} />
      <h3
        className={cn(
          "font-sans text-[20px] font-normal tracking-tight",
          surface === "forest" ? "text-cream" : "text-forest",
        )}
      >
        {title}
      </h3>
      <p
        className={cn(
          "font-flex text-[14px] font-medium leading-tight",
          surface === "forest" ? "text-paper/70" : "text-mute",
        )}
      >
        {body}
      </p>
    </div>
  )
}
